import { fromJS, Map } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import * as log from 'loglevel';
import {
    GenericRequestMessage,
    GenericResponseMessage,
    GenericRequestMessageType,
} from '../genericMessage';
import { MESSAGES_TYPES, INFOTEXTS } from '../../constants';

const augmentInfoText = (response: any, output: any) => {
    let infoText;
    if (output?.turnID?.startsWith('llm-answer')) {
        infoText = INFOTEXTS.LLM_DISCLAIMER;
    }

    return infoText
        ? {
              ...response,
              infoText,
          }
        : response;
};

const convertFromJovoOutput = (output: any) => {
    const convertTextOutput = () => ({
        messageType: MESSAGES_TYPES.TEXT,
        text: output.message,
    });

    const convertButtonOutput = ({ text, variant, value, payload }: any) => {
        if (payload && payload.intent) {
            return {
                type: 'postback',
                title: text,
                variant,
                payload,
            };
        } else if (payload && payload.url) {
            return {
                type: 'web_url',
                title: text,
                variant,
                url: payload.url,
            };
        } else if (payload && payload.internalUrlName) {
            return {
                type: 'web_url_internal',
                title: text,
                variant,
                payload: {
                    urlName: payload.internalUrlName,
                    urlParams: payload.internalUrlParams || {},
                },
            };
        } else {
            return {
                type: 'postback',
                title: text,
                variant,
                payload: value || text,
            };
        }
    };

    const convertQuickRepliesOutput = () => ({
        messageType: MESSAGES_TYPES.BUTTONS,
        text: output.message,
        buttons: (output.buttons && output.buttons.map(convertButtonOutput)) || [],
    });

    const convertImageOutput = () => ({
        messageType: MESSAGES_TYPES.IMGREPLY.IMAGE,
        attachment: {
            type: 'image',
            payload: {
                title: output.title,
                src: output.url,
            },
        },
    });

    const convertVideoOutput = () => ({
        messageType: MESSAGES_TYPES.VIDREPLY.VIDEO,
        attachment: {
            type: 'video',
            payload: {
                title: output.title,
                src: output.url,
            },
        },
    });

    const convertCarouselPanel = ({ title, subtitle, imageUrl, action, buttons }: any) => ({
        title,
        subtitle,
        image_url: imageUrl,
        default_action: {
            type: 'web_url',
            url: action.url,
        },
        buttons: (buttons && buttons.map(convertButtonOutput)) || [],
    });

    const convertCarouselOutput = () => ({
        messageType: MESSAGES_TYPES.CAROUSEL,
        attachment: {
            type: 'template',
            payload: {
                template_type: 'generic',
                elements: (output.panels && output.panels.map(convertCarouselPanel)) || [],
            },
        },
    });

    const converters = Map<string, Function>({
        text: convertTextOutput,
        quickReply: convertQuickRepliesOutput,
        image: convertImageOutput,
        video: convertVideoOutput,
        carousel: convertCarouselOutput,
    });

    const appropriateConverter = converters.get(output.type, () => {
        log.debug('unknown output type', output.type);
        return convertTextOutput;
    });

    return augmentInfoText(appropriateConverter(), output);
};

export const parseRequest = (data: any): GenericRequestMessage => {
    // TBD
    return {
        type: GenericRequestMessageType.TEXT,
        body: 'tbd',
    };
};

export const parseResponse = (data: any): GenericResponseMessage => {
    return {
        output: data?.output?.map(convertFromJovoOutput),
        state: data?.context?.session,
    };
};

export const buildRequest = (message: GenericRequestMessage): any => {
    const data = fromJS({
        version: '1.1.0',
        platform: 'platform-cui-web',
        id: uuidv4(),
        timestamp: new Date().toISOString(),
        timeZone: 'Europe/Berlin',
        locale: message.locale || 'de',
        data: {},
        input: {
            type: message.type,
        },
        context: {
            session: message.state || {},
            application: message.application || {},
            device: message.device || {},
        },
    });

    const requestHandlers = Map({
        [GenericRequestMessageType.TEXT]: () => fromJS({ input: { text: message.body } }),
        [GenericRequestMessageType.APPLICATION_EVENT]: () =>
            fromJS({ input: { event: message.body } }),
        [GenericRequestMessageType.LAUNCH]: () => fromJS({ input: {} }),
        [GenericRequestMessageType.DEEPLINK]: () => fromJS({ input: { deeplink: message.body } }),
    });

    const requestData = requestHandlers.get(message.type, () =>
        fromJS({ input: { nlu: message.body } })
    )();

    return data.mergeDeep(requestData).toJS();
};

export const buildResponse = (message: GenericResponseMessage): any => {
    // TBD
};
